import type { AxisSelection, SourceType } from '@atlas/types'
import { AxisCode, ClassifiedObjectType, FileType } from '@atlas/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { type FileWithTagsDTO, fileService } from '@/services/file.service'
import { getItems, removeItem } from '@/upload/upload-store'
import { useUploadFinal, workOrderFilesKey } from './useWorkOrderFiles'

/** Upload-store owner tag for an order's source files of one `source_type`. */
export const sourceOwner = (orderCode: string, sourceType: SourceType): string => `wo-source:${sourceType}:${orderCode}`

/** Query key for an order's source files of one `source_type`, under {@link workOrderFilesKey} so a file delete reaches it. */
const workOrderSourcesKey = (orderCode: string, sourceType: SourceType) => [
	...workOrderFilesKey(orderCode),
	'source',
	sourceType,
]

/**
 * A work order's source files of one `source_type`: the material the order is made FROM, as
 * opposed to its deliverables. Discovered by `file_type=source` + `source_type` and the
 * order's `order_code` attribute.
 */
export const useWorkOrderSources = (orderCode: string, sourceType: SourceType) =>
	useQuery<FileWithTagsDTO[]>({
		queryKey: workOrderSourcesKey(orderCode, sourceType),
		queryFn: () =>
			fileService.discover({ [AxisCode.FILE_TYPE]: [FileType.SOURCE], [AxisCode.SOURCE_TYPE]: [sourceType] }, {
				[AxisCode.ORDER_CODE]: [orderCode],
			} as AxisSelection),
	})

/**
 * Upload a source file of a given `source_type` to a work order: classified
 * `file_type=source` + `source_type` + the order's tuple, stamped with the `order_code`
 * attribute and audited against the order, like a deliverable but never one — a source file
 * is not part of what the order hands off.
 */
export const useUploadSource = (workOrderId: string, orderCode: string, tuple: AxisSelection, sourceType: SourceType) =>
	useUploadFinal(workOrderId, orderCode, tuple, {
		extraTags: (fileId) => [
			{
				objectType: ClassifiedObjectType.FILE,
				objectId: fileId,
				axisCode: AxisCode.FILE_TYPE,
				valueCode: FileType.SOURCE,
			},
			{
				objectType: ClassifiedObjectType.FILE,
				objectId: fileId,
				axisCode: AxisCode.SOURCE_TYPE,
				valueCode: sourceType,
			},
		],
		owner: sourceOwner(orderCode, sourceType),
	})

/**
 * Delete a source file the user let go of before anyone else saw it, such as a comment draft's
 * image. Unlike `useDeleteWorkOrderFile` it reports success to no one, and it also drops the
 * file's upload-store entry: deleted mid-processing, that entry would never settle and would
 * hold the status bar and the leave-page guard.
 */
export const useDiscardSource = (orderCode: string) => {
	const qc = useQueryClient()
	return (fileId: string) => {
		for (const item of getItems()) if (item.fileId === fileId) removeItem(item.id)
		fileService.deleteFile(fileId).then(
			() => qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) }),
			(error: unknown) => notification.error(apiErrorMessage(error, 'Could not delete the removed image.')),
		)
	}
}
